import { Edge } from 'reactflow';
import { TableData, TableNode, ColumnType } from '../stores/schema-designer-store';

/**
 * Get TypeScript type for a column
 */
const getTypeScriptType = (columnType: ColumnType, nullable: boolean): string => {
  let tsType: string;

  switch (columnType) {
    case 'varchar':
    case 'text':
    case 'uuid':
    case 'enum':
      tsType = 'string';
      break;

    case 'integer':
    case 'bigint':
    case 'float':
    case 'double':
    case 'decimal':
      tsType = 'number';
      break;

    case 'boolean':
      tsType = 'boolean';
      break;

    case 'date':
    case 'datetime':
    case 'timestamp':
    case 'time':
      tsType = 'Date';
      break;

    case 'json':
    case 'jsonb':
      tsType = 'Record<string, any>';
      break;

    case 'array':
      tsType = 'any[]';
      break;

    default:
      tsType = 'any';
  }

  return nullable ? `${tsType} | null` : tsType;
};

/**
 * Convert snake_case to PascalCase
 */
const toPascalCase = (str: string): string => {
  return str
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('');
};

/**
 * Convert snake_case to camelCase
 */
const toCamelCase = (str: string): string => {
  const pascal = toPascalCase(str);
  return pascal.charAt(0).toLowerCase() + pascal.slice(1);
};

/**
 * Generate TypeScript interface for a single table
 */
export const generateTableInterface = (table: TableData): string => {
  const { name, columns, comment } = table;
  const interfaceName = toPascalCase(name);

  const lines: string[] = [];

  // Add comment if exists
  if (comment) {
    lines.push('/**');
    lines.push(` * ${comment}`);
    lines.push(' */');
  }

  lines.push(`export interface ${interfaceName} {`);

  // Generate properties
  columns.forEach((column) => {
    const propertyName = toCamelCase(column.name);
    const propertyType = getTypeScriptType(column.type, column.nullable);

    // Add column comment
    if (column.comment) {
      lines.push(`  /** ${column.comment} */`);
    }

    // Add JSDoc tags for constraints
    const tags: string[] = [];
    if (column.primaryKey) tags.push('@primaryKey');
    if (column.unique) tags.push('@unique');
    if (column.autoIncrement) tags.push('@autoIncrement');
    if (column.defaultValue) tags.push(`@default ${column.defaultValue}`);

    if (tags.length > 0) {
      lines.push(`  /** ${tags.join(' ')} */`);
    }

    lines.push(`  ${propertyName}: ${propertyType};`);
  });

  lines.push('}');

  return lines.join('\n');
};

/**
 * Generate Prisma schema for a table
 */
export const generatePrismaSchema = (table: TableData): string => {
  const { name, columns, comment } = table;
  const modelName = toPascalCase(name);

  const lines: string[] = [];

  // Add comment if exists
  if (comment) {
    lines.push(`/// ${comment}`);
  }

  lines.push(`model ${modelName} {`);

  // Generate fields
  columns.forEach((column) => {
    const fieldName = toCamelCase(column.name);
    let prismaType = '';

    // Map SQL types to Prisma types
    switch (column.type) {
      case 'varchar':
      case 'text':
      case 'uuid':
      case 'enum':
        prismaType = 'String';
        break;
      case 'integer':
        prismaType = 'Int';
        break;
      case 'bigint':
        prismaType = 'BigInt';
        break;
      case 'float':
      case 'double':
      case 'decimal':
        prismaType = 'Float';
        break;
      case 'boolean':
        prismaType = 'Boolean';
        break;
      case 'date':
      case 'datetime':
      case 'timestamp':
        prismaType = 'DateTime';
        break;
      case 'json':
      case 'jsonb':
        prismaType = 'Json';
        break;
      default:
        prismaType = 'String';
    }

    // Add nullable marker
    if (column.nullable) {
      prismaType += '?';
    }

    // Build field attributes
    const attributes: string[] = [];
    if (column.primaryKey) attributes.push('@id');
    if (column.autoIncrement) attributes.push('@default(autoincrement())');
    if (column.unique) attributes.push('@unique');
    if (column.defaultValue && !column.autoIncrement) {
      if (column.defaultValue === 'CURRENT_TIMESTAMP' || column.defaultValue === 'NOW()') {
        attributes.push('@default(now())');
      } else if (column.type === 'uuid' && column.defaultValue === 'uuid_generate_v4()') {
        attributes.push('@default(uuid())');
      } else if (column.type === 'boolean') {
        attributes.push(`@default(${column.defaultValue})`);
      } else if (['integer', 'bigint', 'float', 'double', 'decimal'].includes(column.type)) {
        attributes.push(`@default(${column.defaultValue})`);
      } else {
        attributes.push(`@default("${column.defaultValue}")`);
      }
    }
    if (column.name !== toCamelCase(column.name)) {
      attributes.push(`@map("${column.name}")`);
    }

    const attrString = attributes.length > 0 ? ` ${attributes.join(' ')}` : '';
    lines.push(`  ${fieldName} ${prismaType}${attrString}`);
  });

  lines.push('');
  lines.push(`  @@map("${name}")`);
  lines.push('}');

  return lines.join('\n');
};

/**
 * Generate Drizzle ORM schema for a table
 */
export const generateDrizzleSchema = (table: TableData, dbType: 'postgres' | 'mysql' | 'sqlite' = 'postgres'): string => {
  const { name, columns, comment } = table;
  const tableName = toCamelCase(name);

  const lines: string[] = [];
  const importTypes = new Set<string>();

  // Determine which types to import
  columns.forEach((column) => {
    switch (column.type) {
      case 'varchar':
        importTypes.add('varchar');
        break;
      case 'text':
        importTypes.add('text');
        break;
      case 'integer':
        importTypes.add('integer');
        break;
      case 'bigint':
        importTypes.add('bigint');
        break;
      case 'boolean':
        importTypes.add('boolean');
        break;
      case 'timestamp':
        importTypes.add('timestamp');
        break;
      case 'date':
        importTypes.add('date');
        break;
      case 'time':
        importTypes.add('time');
        break;
      case 'json':
      case 'jsonb':
        importTypes.add('json');
        break;
      case 'uuid':
        importTypes.add('uuid');
        break;
      case 'decimal':
        importTypes.add('decimal');
        break;
      case 'float':
      case 'double':
        importTypes.add('doublePrecision');
        break;
    }
  });

  // Add serial for auto-increment
  const hasAutoIncrement = columns.some((col) => col.autoIncrement);
  if (hasAutoIncrement) {
    if (dbType === 'postgres') importTypes.add('serial');
    else if (dbType === 'mysql') importTypes.add('serial');
    else if (dbType === 'sqlite') importTypes.add('integer');
  }

  // Generate import based on database type
  const tableFunction = dbType === 'postgres' ? 'pgTable' : dbType === 'mysql' ? 'mysqlTable' : 'sqliteTable';
  const dbImport = dbType === 'postgres' ? 'drizzle-orm/pg-core' : dbType === 'mysql' ? 'drizzle-orm/mysql-core' : 'drizzle-orm/sqlite-core';

  if (importTypes.size > 0) {
    lines.push(`import { ${Array.from(importTypes).join(', ')}, ${tableFunction} } from '${dbImport}';`);
  } else {
    lines.push(`import { ${tableFunction} } from '${dbImport}';`);
  }
  lines.push('');

  // Add comment if exists
  if (comment) {
    lines.push(`/** ${comment} */`);
  }

  lines.push(`export const ${tableName} = ${tableFunction}('${name}', {`);

  // Generate columns
  columns.forEach((column, index) => {
    const fieldName = toCamelCase(column.name);
    let drizzleType = '';

    // Map to Drizzle types
    if (column.autoIncrement && column.primaryKey) {
      if (dbType === 'postgres') {
        drizzleType = `serial('${column.name}').primaryKey()`;
      } else if (dbType === 'mysql') {
        drizzleType = `serial('${column.name}').primaryKey()`;
      } else {
        drizzleType = `integer('${column.name}').primaryKey({ autoIncrement: true })`;
      }
    } else {
      switch (column.type) {
        case 'varchar':
          drizzleType = `varchar('${column.name}', { length: ${column.length || 255} })`;
          break;
        case 'text':
          drizzleType = `text('${column.name}')`;
          break;
        case 'integer':
          drizzleType = `integer('${column.name}')`;
          break;
        case 'bigint':
          drizzleType = `bigint('${column.name}', { mode: 'number' })`;
          break;
        case 'boolean':
          drizzleType = `boolean('${column.name}')`;
          break;
        case 'timestamp':
          drizzleType = `timestamp('${column.name}', { mode: 'date' })`;
          break;
        case 'date':
          drizzleType = `date('${column.name}', { mode: 'date' })`;
          break;
        case 'time':
          drizzleType = `time('${column.name}')`;
          break;
        case 'uuid':
          if (dbType === 'postgres') {
            drizzleType = `uuid('${column.name}')`;
          } else {
            drizzleType = `varchar('${column.name}', { length: 36 })`;
          }
          break;
        case 'json':
        case 'jsonb':
          if (dbType === 'postgres') {
            drizzleType = `json('${column.name}')`;
          } else {
            drizzleType = `json('${column.name}')`;
          }
          break;
        case 'decimal':
          drizzleType = `decimal('${column.name}', { precision: ${column.precision || 10}, scale: ${column.scale || 2} })`;
          break;
        case 'float':
        case 'double':
          if (dbType === 'postgres') {
            drizzleType = `doublePrecision('${column.name}')`;
          } else {
            drizzleType = `float('${column.name}')`;
          }
          break;
        default:
          drizzleType = `text('${column.name}')`;
      }

      // Add modifiers
      if (column.primaryKey && !column.autoIncrement) {
        drizzleType += '.primaryKey()';
      }
      if (column.unique) {
        drizzleType += '.unique()';
      }
      if (!column.nullable) {
        drizzleType += '.notNull()';
      }
      if (column.defaultValue && !column.autoIncrement) {
        if (column.defaultValue === 'CURRENT_TIMESTAMP' || column.defaultValue === 'NOW()') {
          drizzleType += '.defaultNow()';
        } else if (['integer', 'bigint', 'float', 'double', 'decimal'].includes(column.type)) {
          drizzleType += `.default(${column.defaultValue})`;
        } else if (column.type === 'boolean') {
          drizzleType += `.default(${column.defaultValue})`;
        } else {
          drizzleType += `.default('${column.defaultValue}')`;
        }
      }
    }

    const comma = index < columns.length - 1 ? ',' : '';
    lines.push(`  ${fieldName}: ${drizzleType}${comma}`);
  });

  lines.push('});');

  return lines.join('\n');
};

/**
 * Generate all TypeScript interfaces
 */
export const generateTypeScriptInterfaces = (nodes: TableNode[]): string => {
  if (!nodes.length) {
    return '// No tables defined yet. Add some tables to generate TypeScript interfaces.';
  }

  const interfaces = nodes.map((node) => generateTableInterface(node.data));

  return [
    '/**',
    ' * Auto-generated TypeScript interfaces from schema designer',
    ` * Generated at: ${new Date().toISOString()}`,
    ' */',
    '',
    ...interfaces,
  ].join('\n\n');
};

/**
 * Generate complete Prisma schema with relationships
 */
export const generateCompletePrismaSchema = (nodes: TableNode[], edges: Edge[]): string => {
  if (!nodes.length) {
    return '// No tables defined yet. Add some tables to generate Prisma schema.';
  }

  // Generate base models
  const models = nodes.map((node) => {
    const lines = generatePrismaSchema(node.data).split('\n');

    // Add relationship fields if any edges connect to this node
    const relationships: string[] = [];
    edges.forEach((edge) => {
      const sourceNode = nodes.find((n) => n.id === edge.source);
      const targetNode = nodes.find((n) => n.id === edge.target);

      if (sourceNode?.data.name === node.data.name && targetNode) {
        const relationName = toCamelCase(targetNode.data.name);
        relationships.push(`  ${relationName} ${toPascalCase(targetNode.data.name)}? @relation(fields: [${relationName}Id], references: [id])`);
        relationships.push(`  ${relationName}Id Int?`);
      }

      if (targetNode?.data.name === node.data.name && sourceNode) {
        const relationName = toPascalCase(sourceNode.data.name);
        relationships.push(`  ${toCamelCase(sourceNode.data.name)}s ${relationName}[]`);
      }
    });

    // Insert relationships before the closing brace
    if (relationships.length > 0) {
      const closingBraceIndex = lines.lastIndexOf('}');
      lines.splice(closingBraceIndex, 0, '', ...relationships);
    }

    return lines.join('\n');
  });

  return [
    'generator client {',
    '  provider = "prisma-client-js"',
    '}',
    '',
    'datasource db {',
    '  provider = "postgresql"',
    '  url      = env("DATABASE_URL")',
    '}',
    '',
    ...models,
  ].join('\n\n');
};

/**
 * Generate complete Drizzle schema with consolidated imports
 */
export const generateCompleteDrizzleSchema = (nodes: TableNode[], dbType: 'postgres' | 'mysql' | 'sqlite' = 'postgres'): string => {
  if (!nodes.length) {
    return '// No tables defined yet. Add some tables to generate Drizzle schema.';
  }

  const allImportTypes = new Set<string>();

  // Collect all import types needed across all tables
  nodes.forEach((node) => {
    node.data.columns.forEach((column) => {
      switch (column.type) {
        case 'varchar':
          allImportTypes.add('varchar');
          break;
        case 'text':
          allImportTypes.add('text');
          break;
        case 'integer':
          allImportTypes.add('integer');
          break;
        case 'bigint':
          allImportTypes.add('bigint');
          break;
        case 'boolean':
          allImportTypes.add('boolean');
          break;
        case 'timestamp':
          allImportTypes.add('timestamp');
          break;
        case 'date':
          allImportTypes.add('date');
          break;
        case 'time':
          allImportTypes.add('time');
          break;
        case 'json':
        case 'jsonb':
          allImportTypes.add('json');
          break;
        case 'uuid':
          allImportTypes.add('uuid');
          break;
        case 'decimal':
          allImportTypes.add('decimal');
          break;
        case 'float':
        case 'double':
          allImportTypes.add('doublePrecision');
          break;
      }
      if (column.autoIncrement) {
        allImportTypes.add('serial');
      }
    });
  });

  // Generate consolidated import
  const tableFunction = dbType === 'postgres' ? 'pgTable' : dbType === 'mysql' ? 'mysqlTable' : 'sqliteTable';
  const dbImport = dbType === 'postgres' ? 'drizzle-orm/pg-core' : dbType === 'mysql' ? 'drizzle-orm/mysql-core' : 'drizzle-orm/sqlite-core';
  const importStatement = `import { ${Array.from(allImportTypes).sort().join(', ')}, ${tableFunction} } from '${dbImport}';`;

  // Generate table definitions without individual imports
  const tableDefinitions = nodes.map((node) => {
    const { name, columns, comment } = node.data;
    const tableName = toCamelCase(name);
    const lines: string[] = [];

    if (comment) {
      lines.push(`/** ${comment} */`);
    }

    lines.push(`export const ${tableName} = ${tableFunction}('${name}', {`);

    columns.forEach((column, index) => {
      const fieldName = toCamelCase(column.name);
      let drizzleType = '';

      if (column.autoIncrement && column.primaryKey) {
        if (dbType === 'postgres' || dbType === 'mysql') {
          drizzleType = `serial('${column.name}').primaryKey()`;
        } else {
          drizzleType = `integer('${column.name}').primaryKey({ autoIncrement: true })`;
        }
      } else {
        switch (column.type) {
          case 'varchar':
            drizzleType = `varchar('${column.name}', { length: ${column.length || 255} })`;
            break;
          case 'text':
            drizzleType = `text('${column.name}')`;
            break;
          case 'integer':
            drizzleType = `integer('${column.name}')`;
            break;
          case 'bigint':
            drizzleType = `bigint('${column.name}', { mode: 'number' })`;
            break;
          case 'boolean':
            drizzleType = `boolean('${column.name}')`;
            break;
          case 'timestamp':
            drizzleType = `timestamp('${column.name}', { mode: 'date' })`;
            break;
          case 'date':
            drizzleType = `date('${column.name}', { mode: 'date' })`;
            break;
          case 'time':
            drizzleType = `time('${column.name}')`;
            break;
          case 'uuid':
            drizzleType = dbType === 'postgres' ? `uuid('${column.name}')` : `varchar('${column.name}', { length: 36 })`;
            break;
          case 'json':
          case 'jsonb':
            drizzleType = `json('${column.name}')`;
            break;
          case 'decimal':
            drizzleType = `decimal('${column.name}', { precision: ${column.precision || 10}, scale: ${column.scale || 2} })`;
            break;
          case 'float':
          case 'double':
            drizzleType = dbType === 'postgres' ? `doublePrecision('${column.name}')` : `float('${column.name}')`;
            break;
          default:
            drizzleType = `text('${column.name}')`;
        }

        if (column.primaryKey && !column.autoIncrement) {
          drizzleType += '.primaryKey()';
        }
        if (column.unique) {
          drizzleType += '.unique()';
        }
        if (!column.nullable) {
          drizzleType += '.notNull()';
        }
        if (column.defaultValue && !column.autoIncrement) {
          if (column.defaultValue === 'CURRENT_TIMESTAMP' || column.defaultValue === 'NOW()') {
            drizzleType += '.defaultNow()';
          } else if (['integer', 'bigint', 'float', 'double', 'decimal'].includes(column.type)) {
            drizzleType += `.default(${column.defaultValue})`;
          } else if (column.type === 'boolean') {
            drizzleType += `.default(${column.defaultValue})`;
          } else {
            drizzleType += `.default('${column.defaultValue}')`;
          }
        }
      }

      const comma = index < columns.length - 1 ? ',' : '';
      lines.push(`  ${fieldName}: ${drizzleType}${comma}`);
    });

    lines.push('});');
    return lines.join('\n');
  });

  return [
    '/**',
    ' * Auto-generated Drizzle ORM schema from schema designer',
    ` * Generated at: ${new Date().toISOString()}`,
    ` * Database: ${dbType}`,
    ' */',
    '',
    importStatement,
    '',
    ...tableDefinitions,
  ].join('\n\n');
};

/**
 * Generate Zod schema for validation
 */
export const generateZodSchema = (table: TableData): string => {
  const { name, columns } = table;
  const schemaName = `${toCamelCase(name)}Schema`;

  const lines: string[] = [];
  lines.push("import { z } from 'zod';");
  lines.push('');
  lines.push(`export const ${schemaName} = z.object({`);

  columns.forEach((column, index) => {
    const fieldName = toCamelCase(column.name);
    let zodType = '';

    switch (column.type) {
      case 'varchar':
      case 'text':
      case 'uuid':
      case 'enum':
        zodType = 'z.string()';
        if (column.length) {
          zodType = `z.string().max(${column.length})`;
        }
        break;
      case 'integer':
      case 'bigint':
      case 'float':
      case 'double':
      case 'decimal':
        zodType = 'z.number()';
        break;
      case 'boolean':
        zodType = 'z.boolean()';
        break;
      case 'date':
      case 'datetime':
      case 'timestamp':
        zodType = 'z.date()';
        break;
      case 'json':
      case 'jsonb':
        zodType = 'z.record(z.any())';
        break;
      case 'array':
        zodType = 'z.array(z.any())';
        break;
      default:
        zodType = 'z.any()';
    }

    // Add nullable/optional
    if (column.nullable) {
      zodType += '.nullable()';
    }
    if (column.defaultValue) {
      zodType += '.optional()';
    }

    const comma = index < columns.length - 1 ? ',' : '';
    lines.push(`  ${fieldName}: ${zodType}${comma}`);
  });

  lines.push('});');
  lines.push('');
  lines.push(`export type ${toPascalCase(name)} = z.infer<typeof ${schemaName}>;`);

  return lines.join('\n');
};

/**
 * Generate complete Zod schemas for all tables
 */
export const generateCompleteZodSchemas = (nodes: TableNode[]): string => {
  if (!nodes.length) {
    return '// No tables defined yet. Add some tables to generate Zod schemas.';
  }

  const schemas = nodes.map((node) => generateZodSchema(node.data));

  return [
    '/**',
    ' * Auto-generated Zod validation schemas from schema designer',
    ` * Generated at: ${new Date().toISOString()}`,
    ' */',
    '',
    ...schemas,
  ].join('\n\n');
};
